import React, { useState, useEffect, useCallback } from "react";

// ── Data Analysis ────────────────────────────────────────────────────────────
// Read-only analytics for an OPTION SELLER over the stored historical data:
//   • Daily (Spot)  — day-wise index OHLC, all-time high/low, daily range,
//     gap-up/down, per-weekday behaviour, up/down streak.
//   • IV / Straddle — day-wise ATM straddle premium + Black-Scholes implied vol.
// Both read the same store the backtester uses, so no live broker token is needed.

// Crypto portal (crypto.*) shares this page but with BTC/ETH, USD formatting and a
// dark theme; the NSE portal keeps the light indices theme.
const IS_CRYPTO_PORTAL = typeof window !== "undefined" && /^crypto\./i.test(window.location.hostname || "");
const C = IS_CRYPTO_PORTAL
  ? { card: "#181a20", border: "#2b3139", text: "#eaecef", sub: "#b7bdc6", muted: "#848e9c", blue: "#2c8cff", indigo: "#7a5cff", green: "#0ecb81", red: "#f6465d", amber: "#f0b90b", bg: "#0b0e11", th: "#12151a", inputBg: "#0f1216" }
  : { card: "#fff", border: "#e8edf3", text: "#0f172a", sub: "#64748b", muted: "#94a3b8", blue: "#2563eb", indigo: "#4f46e5", green: "#10b981", red: "#ef4444", amber: "#f59e0b", bg: "#f8fafc", th: "#fbfcfe", inputBg: "#fff" };
const card = (extra = {}) => ({ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, ...extra });
const sel = (extra = {}) => ({ padding: "9px 11px", border: `1px solid ${C.border}`, borderRadius: 9, fontSize: 14, background: C.inputBg, color: C.text, cursor: "pointer", ...extra });
const UNDERLYINGS = IS_CRYPTO_PORTAL ? ["BTC", "ETH"] : ["NIFTY", "BANKNIFTY", "FINNIFTY", "MIDCPNIFTY", "SENSEX"];

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: "Bearer " + token } : {}) };
};
const iso = (d) => d.toISOString().slice(0, 10);
// NSE numbers use the en-IN grouping; crypto uses plain grouping + a $ prefix on prices.
const nfmt = (n, d = 0) => (n == null || isNaN(n) ? "—" : Number(n).toLocaleString(IS_CRYPTO_PORTAL ? "en-US" : "en-IN", { minimumFractionDigits: d, maximumFractionDigits: d }));
const cur = (n, d = 0) => (n == null || isNaN(n) ? "—" : (IS_CRYPTO_PORTAL ? "$" : "") + nfmt(n, d));
const pct = (n, d = 2) => (n == null || isNaN(n) ? "—" : `${n > 0 ? "+" : ""}${Number(n).toFixed(d)}%`);
const signCol = (n) => (n > 0 ? C.green : n < 0 ? C.red : C.sub);

// A summary stat tile.
function Tile({ label, value, sub, color, help }) {
  return (
    <div style={card({ padding: 16 })} title={help || ""}>
      <div style={{ fontSize: 10.5, color: C.muted, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", marginBottom: 7 }}>{label}</div>
      <div style={{ fontSize: 21, fontWeight: 900, color: color || C.text, lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11.5, color: C.muted, marginTop: 5, fontWeight: 600 }}>{sub}</div>}
    </div>
  );
}

const th = { textAlign: "right", padding: "9px 10px", fontSize: 11, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: "0.03em", borderBottom: `2px solid ${C.border}`, whiteSpace: "nowrap", position: "sticky", top: 0, background: C.th };
const thL = { ...th, textAlign: "left" };
const td = { textAlign: "right", padding: "8px 10px", fontSize: 13, color: C.text, borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" };
const tdL = { ...td, textAlign: "left" };
const fInput = { width: "100%", boxSizing: "border-box", padding: "4px 6px", border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 11.5, background: C.inputBg, color: C.text, fontVariantNumeric: "tabular-nums" };

// ── FilterableTable ──────────────────────────────────────────────────────────
// Generic sortable + per-column filtered table. `columns` = descriptors:
//   { key, label, align?, bold?, num?, fmt?(v,row), color?(v,row),
//     filter?: 'range' | 'select' | 'text' }
// Click a header to sort by it (toggles ▼/▲); a toggleable filter row gives each
// column its own control — min/max for numbers, a value picker or text-contains for
// the rest. All client-side over the already-loaded rows.
function FilterableTable({ columns, rows, initialSort, minWidth = 780, maxHeight = 620, empty = "No rows" }) {
  const [sortKey, setSortKey] = useState(initialSort?.key || columns[0].key);
  const [sortDir, setSortDir] = useState(initialSort?.dir || "desc"); // 'asc' | 'desc'
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({}); // key → {min,max} | value | text

  const setF = (key, patch) => setFilters((f) => ({ ...f, [key]: typeof patch === "object" && patch !== null ? { ...f[key], ...patch } : patch }));
  const clearAll = () => setFilters({});
  const activeCount = columns.reduce((n, c) => {
    const v = filters[c.key];
    if (c.filter === "range") return n + ((v && (v.min !== "" && v.min != null)) || (v && (v.max !== "" && v.max != null)) ? 1 : 0);
    return n + (v != null && v !== "" ? 1 : 0);
  }, 0);

  const onSort = (key) => {
    if (key === sortKey) setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    else { setSortKey(key); setSortDir("desc"); }
  };

  // Distinct values for 'select' columns (sorted).
  const distinct = (key) => [...new Set(rows.map((r) => r[key]).filter((v) => v != null && v !== ""))].sort();

  const filtered = rows.filter((r) => columns.every((c) => {
    const v = filters[c.key];
    if (v == null || v === "") return true;
    const cell = r[c.key];
    if (c.filter === "range") {
      if ((v.min == null || v.min === "") && (v.max == null || v.max === "")) return true;
      const num = Number(cell);
      if (cell == null || isNaN(num)) return false; // a set numeric filter excludes blanks (e.g. null IV)
      if (v.min !== "" && v.min != null && num < Number(v.min)) return false;
      if (v.max !== "" && v.max != null && num > Number(v.max)) return false;
      return true;
    }
    if (c.filter === "select") return String(cell) === String(v);
    if (c.filter === "text") return String(cell ?? "").toLowerCase().includes(String(v).toLowerCase());
    return true;
  }));

  const sorted = [...filtered].sort((a, b) => {
    const col = columns.find((c) => c.key === sortKey) || columns[0];
    let av = a[sortKey], bv = b[sortKey];
    if (col.num) { av = av == null ? -Infinity : Number(av); bv = bv == null ? -Infinity : Number(bv); return sortDir === "asc" ? av - bv : bv - av; }
    av = String(av ?? ""); bv = String(bv ?? "");
    return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
  });

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontSize: 12.5, color: C.sub, fontWeight: 700 }}>
          Showing {sorted.length}{sorted.length !== rows.length ? ` / ${rows.length}` : ""} rows
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {activeCount > 0 && <button onClick={clearAll} style={{ ...sel({ padding: "6px 10px", fontSize: 12, fontWeight: 700, color: C.red }) }}>✕ Clear</button>}
          <button onClick={() => setShowFilters((s) => !s)} style={{ ...sel({ padding: "6px 10px", fontSize: 12, fontWeight: 700, color: showFilters || activeCount ? C.blue : C.sub, borderColor: showFilters || activeCount ? C.blue : C.border }) }}>
            🔎 Filters{activeCount ? ` (${activeCount})` : ""}
          </button>
        </div>
      </div>
      <div style={{ overflowX: "auto", maxHeight }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth }}>
          <thead>
            <tr>
              {columns.map((c) => {
                const base = c.align === "left" ? thL : th;
                const active = c.key === sortKey;
                return (
                  <th key={c.key} style={{ ...base, cursor: "pointer", color: active ? C.blue : base.color, userSelect: "none" }} onClick={() => onSort(c.key)} title="Sort">
                    {c.label}{active ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
                  </th>
                );
              })}
            </tr>
            {showFilters && (
              <tr>
                {columns.map((c) => {
                  const base = c.align === "left" ? thL : th;
                  const cellStyle = { ...base, top: 34, paddingTop: 5, paddingBottom: 7, textTransform: "none", fontWeight: 600, borderBottom: `1px solid ${C.border}` };
                  return (
                    <th key={c.key} style={cellStyle}>
                      {c.filter === "range" && (
                        <div style={{ display: "flex", gap: 3 }}>
                          <input type="number" placeholder="min" value={filters[c.key]?.min ?? ""} onChange={(e) => setF(c.key, { min: e.target.value })} style={fInput} />
                          <input type="number" placeholder="max" value={filters[c.key]?.max ?? ""} onChange={(e) => setF(c.key, { max: e.target.value })} style={fInput} />
                        </div>
                      )}
                      {c.filter === "select" && (
                        <select value={filters[c.key] ?? ""} onChange={(e) => setF(c.key, e.target.value)} style={{ ...fInput, cursor: "pointer" }}>
                          <option value="">All</option>
                          {distinct(c.key).map((v) => <option key={v} value={v}>{v}</option>)}
                        </select>
                      )}
                      {c.filter === "text" && (
                        <input type="text" placeholder="search" value={filters[c.key] ?? ""} onChange={(e) => setF(c.key, e.target.value)} style={fInput} />
                      )}
                    </th>
                  );
                })}
              </tr>
            )}
          </thead>
          <tbody>
            {sorted.map((r, i) => (
              <tr key={r.date || i}>
                {columns.map((c) => {
                  const base = c.align === "left" ? tdL : td;
                  const val = r[c.key];
                  const col = c.color ? c.color(val, r) : undefined;
                  return <td key={c.key} style={{ ...base, ...(col ? { color: col } : {}), ...(c.bold ? { fontWeight: 700 } : {}) }}>{c.fmt ? c.fmt(val, r) : (val ?? "—")}</td>;
                })}
              </tr>
            ))}
            {!sorted.length && (
              <tr><td colSpan={columns.length} style={{ ...tdL, textAlign: "center", color: C.muted, padding: 24 }}>{empty}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function DataAnalysis() {
  const [underlying, setUnderlying] = useState(UNDERLYINGS[0]);
  const [view, setView] = useState("daily"); // 'daily' | 'iv'
  const today = new Date();
  const yearAgo = new Date(Date.now() - 365 * 864e5);
  const [from, setFrom] = useState(iso(yearAgo));
  const [to, setTo] = useState(iso(today));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    setLoading(true); setErr(""); setData(null);
    const path = view === "iv" ? "iv" : "daily";
    fetch(`/api/data-analysis/${path}/${underlying}?from=${from}&to=${to}`, { headers: getHeaders() })
      .then((r) => r.json().then((j) => ({ ok: r.ok, j })))
      .then(({ ok, j }) => { if (!ok) { setErr(j.message || "Failed to load"); } else setData(j); })
      .catch(() => setErr("Network error"))
      .finally(() => setLoading(false));
  }, [underlying, view, from, to]);

  useEffect(() => { load(); }, [load]);

  const quickRange = (days) => { setFrom(iso(new Date(Date.now() - days * 864e5))); setTo(iso(new Date())); };

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 18 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 900, margin: 0, color: C.text }}>📊 Data Analysis</h1>
          <div style={{ fontSize: 13, color: C.sub, marginTop: 3 }}>Option-seller ke liye — daily high/low, range, gap, per-weekday move aur day-wise IV / straddle.</div>
        </div>
      </div>

      {/* Controls */}
      <div style={card({ padding: 14, marginBottom: 16, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" })}>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: C.sub }}>{IS_CRYPTO_PORTAL ? "Coin" : "Index"}</span>
          <select value={underlying} onChange={(e) => setUnderlying(e.target.value)} style={sel()}>
            {UNDERLYINGS.map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: C.sub }}>From</span>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={sel()} />
          <span style={{ fontSize: 12, fontWeight: 700, color: C.sub }}>To</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={sel()} />
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {[["1M", 30], ["3M", 90], ["6M", 180], ["1Y", 365], ["2Y", 730]].map(([lbl, d]) => (
            <button key={lbl} onClick={() => quickRange(d)} style={{ ...sel({ padding: "7px 11px", fontSize: 12, fontWeight: 700, color: C.blue }) }}>{lbl}</button>
          ))}
        </div>
        <div style={{ flex: 1 }} />
        {/* View toggle */}
        <div style={{ display: "flex", background: C.bg, borderRadius: 10, padding: 3 }}>
          {[["daily", "📈 Daily (Spot)"], ["iv", "🌡️ IV / Straddle"]].map(([id, lbl]) => (
            <button key={id} onClick={() => setView(id)} style={{ border: "none", cursor: "pointer", borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 700, background: view === id ? C.card : "transparent", color: view === id ? C.blue : C.sub, boxShadow: view === id ? "0 1px 3px rgba(0,0,0,0.1)" : "none" }}>{lbl}</button>
          ))}
        </div>
        <button onClick={load} style={{ ...sel({ background: C.blue, color: "#fff", fontWeight: 800, border: "none" }) }}>↻ Reload</button>
      </div>

      {/* Today's expected-move estimate (self-fetches; independent of the range/view). */}
      <ExpectedMove underlying={underlying} />

      {loading && <div style={card({ padding: 50, textAlign: "center", color: C.muted })}>Loading {underlying} data…</div>}
      {err && !loading && (
        <div style={card({ padding: 40, textAlign: "center" })}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>📭</div>
          <div style={{ fontSize: 15, fontWeight: 800, color: C.text, marginBottom: 6 }}>{err}</div>
          <div style={{ fontSize: 13, color: C.sub }}>Data Backtesting / Historical Data tab se download karein, phir yahan dobara try karein.</div>
        </div>
      )}

      {!loading && !err && data && view === "daily" && <DailyView data={data} />}
      {!loading && !err && data && view === "iv" && <IvView data={data} />}
    </div>
  );
}

// ── "Aaj ka Expected Move" panel ─────────────────────────────────────────────
// Forward-looking (ATM IV + straddle) blended with recent realised range to estimate
// TODAY's likely daily range + ±move band. Magnitude only — direction not predicted.
function ExpectedMove({ underlying }) {
  const [em, setEm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true); setErr(""); setEm(null);
    fetch(`/api/data-analysis/expected-move/${underlying}`, { headers: getHeaders() })
      .then((r) => r.json().then((j) => ({ ok: r.ok, j })))
      .then(({ ok, j }) => { if (!alive) return; if (!ok) setErr(j.message || "No estimate"); else setEm(j); })
      .catch(() => { if (alive) setErr("Network error"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [underlying]);

  if (loading) return <div style={card({ padding: 18, marginBottom: 16, color: C.muted, fontSize: 13 })}>Estimating today's move…</div>;
  if (err || !em) return null; // silently hide if no data (e.g. option/spot not downloaded)

  const iv = em.ivImplied, st = em.straddleImplied, h = em.historical, R = em.iv || {};
  const regMap = { low: { t: "LOW VOL", c: C.green }, normal: { t: "NORMAL VOL", c: C.amber }, high: { t: "HIGH VOL", c: C.red }, unknown: { t: "HISTORY ONLY", c: C.muted } };
  const reg = regMap[R.regime] || regMap.unknown;
  const dt = new Date(em.predictFor + "T12:00:00Z");
  const dstr = `${em.weekday} · ${dt.getUTCDate()} ${["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][dt.getUTCMonth()]}`;
  const signed = (n) => (n == null ? "—" : `±${cur(n, 0)}`);

  // Band bar: low2sd … high2sd, spot centred; the inner 25–75% span = ±1σ.
  const band = iv && (
    <div style={{ margin: "4px 0 2px" }}>
      <div style={{ position: "relative", height: 42, borderRadius: 9, background: `linear-gradient(90deg, ${C.red}22, ${C.bg} 25%, ${C.bg} 75%, ${C.green}22)`, border: `1px solid ${C.border}`, overflow: "hidden" }}>
        <div style={{ position: "absolute", left: "25%", right: "25%", top: 0, bottom: 0, background: `${C.blue}1f`, borderLeft: `1px dashed ${C.blue}66`, borderRight: `1px dashed ${C.blue}66` }} />
        <div style={{ position: "absolute", left: "50%", top: 0, bottom: 0, width: 2, background: C.blue, transform: "translateX(-1px)" }} />
        <div style={{ position: "absolute", left: "50%", top: 4, transform: "translateX(-50%)", fontSize: 11, fontWeight: 800, color: C.blue }}>spot {cur(em.spot, 0)}</div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10.5, color: C.muted, fontWeight: 700, marginTop: 4, fontVariantNumeric: "tabular-nums" }}>
        <span>{cur(iv.low2sd, 0)}</span><span>{cur(iv.low1sd, 0)}</span><span style={{ color: C.sub }}>±1σ (68%)</span><span>{cur(iv.high1sd, 0)}</span><span>{cur(iv.high2sd, 0)}</span>
      </div>
    </div>
  );

  const mini = (label, value, sub, color) => (
    <div style={{ padding: "9px 12px", borderRadius: 10, background: C.bg, border: `1px solid ${C.border}`, minWidth: 96, flex: "1 1 96px" }}>
      <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 900, color: color || C.text }}>{value}</div>
      {sub && <div style={{ fontSize: 10.5, color: C.muted, marginTop: 2 }}>{sub}</div>}
    </div>
  );

  return (
    <div style={card({ padding: 18, marginBottom: 16, borderColor: C.blue + "55", background: `linear-gradient(180deg, ${C.blue}0d, ${C.card})` })}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
        <span style={{ fontSize: 16, fontWeight: 900, color: C.text }}>🎯 {underlying} — Aaj ka Expected Move</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: C.sub }}>{dstr}</span>
        <span style={{ marginLeft: "auto", fontSize: 11, fontWeight: 800, padding: "4px 10px", borderRadius: 20, background: reg.c + "22", color: reg.c, letterSpacing: "0.03em" }}>{reg.t}</span>
      </div>

      {/* Headline: blended expected daily range */}
      <div style={{ display: "flex", gap: 18, flexWrap: "wrap", alignItems: "flex-end", marginBottom: band ? 14 : 8 }}>
        <div>
          <div style={{ fontSize: 10.5, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>Expected daily range</div>
          <div style={{ fontSize: 30, fontWeight: 900, color: C.blue, lineHeight: 1.05 }}>{cur(em.blended?.expectedRange, 0)} <span style={{ fontSize: 16, color: C.sub }}>pts</span></div>
          <div style={{ fontSize: 12, color: C.sub, fontWeight: 700 }}>≈ {em.blended?.expectedRangePct}% of spot · high-low</div>
        </div>
        {iv && (
          <div style={{ fontSize: 12.5, color: C.sub, lineHeight: 1.7 }}>
            <div><b style={{ color: C.text }}>68%</b> chance move within <b style={{ color: C.text }}>{signed(iv.move1sd)}</b> &nbsp;({cur(iv.low1sd, 0)} – {cur(iv.high1sd, 0)})</div>
            <div><b style={{ color: C.text }}>95%</b> chance move within <b style={{ color: C.text }}>{signed(iv.move2sd)}</b> &nbsp;({cur(iv.low2sd, 0)} – {cur(iv.high2sd, 0)})</div>
          </div>
        )}
      </div>

      {band}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
        {iv && mini("IV 1σ move", signed(iv.move1sd), `${iv.move1sdPct}%/day`, C.blue)}
        {R.calMult != null && mini("Cal ×1σ", `${R.calMult}×`, "EWMA 68th-%ile", C.amber)}
        {st && mini("ATM straddle", signed(st.move), st.pct != null ? `${st.pct}% expected` : null, C.indigo)}
        {mini("Hist avg range", cur(h.avgRange, 0), `${h.avgRangePct}% · ${h.n}d`)}
        {h.weekdayAvgRange != null && mini(`${em.weekday} avg range`, cur(h.weekdayAvgRange, 0), `${h.weekdayN} ${em.weekday}s`)}
        {mini("Avg abs change", cur(h.avgAbsChange, 0), "magnitude", C.sub)}
        {R.smoothed != null && mini("ATM IV (5d)", `${R.smoothed}%`, `hist avg ${R.histAvg}%`, reg.c)}
      </div>

      <div style={{ fontSize: 11.5, color: C.muted, marginTop: 12, lineHeight: 1.5 }}>
        ⚠️ Sirf <b>magnitude</b> (kitne points) ka anumaan — <b>direction (up/down) nahi</b>. Vol move ka <i>size</i> batata hai, sign nahi. Ye probability band hai, guarantee nahi (news/gap din bahar ja sakta hai).
        {R.calMult != null && <> IV band <b>fat-tail + EWMA calibrated</b> (empirical 68/95th %ile, ×{R.calMult} on raw σ) — Day-wise table me hit-rate check karein (~68% / ~95%).</>}
        {!iv && <> Option data na hone se <b>sirf historical</b> estimate hai — IV band ke liye {underlying} option data download karein.</>}
      </div>
    </div>
  );
}

// ── Daily (spot) view ────────────────────────────────────────────────────────
function DailyView({ data }) {
  const s = data.summary || {};
  const days = data.days || [];
  const columns = [
    { key: "date", label: "Date", align: "left", filter: "text", fmt: (v, r) => { if (!v) return "—"; const [y, m, d] = String(v).split("-"); const MO = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][+m - 1] || m; return `${y}-${MO}-${d}${r?.dow ? " " + r.dow : ""}`; } },
    { key: "open", label: "Open", num: true, filter: "range", fmt: (v) => cur(v, 2) },
    { key: "high", label: "High", num: true, filter: "range", fmt: (v) => cur(v, 2), color: () => C.green },
    { key: "low", label: "Low", num: true, filter: "range", fmt: (v) => cur(v, 2), color: () => C.red },
    { key: "close", label: "Close", num: true, filter: "range", fmt: (v) => cur(v, 2), bold: true },
    { key: "range", label: "Range", num: true, filter: "range", fmt: (v) => cur(v, 2) },
    { key: "rangePct", label: "Range %", num: true, filter: "range", fmt: (v) => `${nfmt(v, 2)}%` },
    { key: "change", label: "Change", num: true, filter: "range", fmt: (v) => (v > 0 ? "+" : "") + cur(v, 2), color: (v) => signCol(v) },
    { key: "changePct", label: "Change %", num: true, filter: "range", fmt: (v) => pct(v), color: (v) => signCol(v), bold: true },
    { key: "expMove", label: "Exp ±1σ (≈Chg)", num: true, filter: "range", fmt: (v, r) => (v != null ? `±${cur(v, 0)}${r?.expSource === "hist" ? " ᴴ" : ""}` : "—"), color: (v, r) => (r?.expSource === "hist" ? C.amber : C.indigo), help: "Expected ±1σ (68%) move. IV-based (prev-day ATM IV × calibration) where option data exists; ᴴ = history-estimated (recent realised σ) when IV missing that day. Empirically ≈ the day's close-to-close CHANGE." },
    { key: "expMove2", label: "Exp ±2σ (≈Rng)", num: true, filter: "range", fmt: (v, r) => (v != null ? `±${cur(v, 0)}${r?.expSource === "hist" ? " ᴴ" : ""}` : "—"), color: (v, r) => (r?.expSource === "hist" ? C.amber : C.indigo), help: "±2σ (95%) band = 2× the ±1σ move. Empirically ≈ the day's HIGH-LOW RANGE. ᴴ = history-estimated (IV missing that day)." },
    { key: "within1sd", label: "In 1σ (Chg)", align: "left", fmt: (v) => (v == null ? "—" : v ? "✓" : "✗"), color: (v) => (v == null ? C.muted : v ? C.green : C.red), bold: true, help: "✓ = actual close-to-close CHANGE stayed within the ±1σ (68%) band." },
    { key: "rangeIn2sd", label: "In 2σ (Rng)", align: "left", fmt: (v) => (v == null ? "—" : v ? "✓" : "✗"), color: (v) => (v == null ? C.muted : v ? C.green : C.red), bold: true, help: "✓ = actual HIGH-LOW RANGE stayed within the ±2σ band. Since ±2σ ≈ median range, ~half the days exceed it (✗ = wider-than-typical range)." },
    { key: "volume", label: "Volume", num: true, filter: "range", fmt: (v) => (v ? nfmt(v, 0) : "—"), color: () => C.sub },
    { key: "vix", label: "VIX", num: true, filter: "range", fmt: (v) => (v != null ? `${nfmt(v, 1)}%` : "—"), color: () => C.indigo, bold: true },
  ];

  return (
    <div>
      {/* Summary tiles */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))", gap: 12, marginBottom: 14 }}>
        <Tile label="All-Time High" value={cur(s.allTimeHigh?.value)} sub={s.allTimeHigh?.date} color={C.green} help="Highest high in range" />
        <Tile label="All-Time Low" value={cur(s.allTimeLow?.value)} sub={s.allTimeLow?.date} color={C.red} help="Lowest low in range" />
        <Tile label="Last Close" value={cur(s.lastClose)} sub={`Period ${pct(s.periodChangePct)}`} color={signCol(s.periodChangePct)} />
        <Tile label="Avg Daily Range" value={cur(s.avgRange)} sub={`${pct(s.avgRangePct)} · median ${cur(s.medianRange)}`} />
        <Tile label="Widest Day" value={cur(s.widestDay?.range)} sub={`${s.widestDay?.date} · ${pct(s.widestDay?.rangePct)}`} color={C.amber} />
        <Tile label="Quietest Day" value={cur(s.quietestDay?.range)} sub={`${s.quietestDay?.date} · ${pct(s.quietestDay?.rangePct)}`} />
        <Tile label="Biggest Up Day" value={pct(s.biggestUp?.changePct)} sub={s.biggestUp?.date} color={C.green} />
        <Tile label="Biggest Down Day" value={pct(s.biggestDown?.changePct)} sub={s.biggestDown?.date} color={C.red} />
        <Tile label="Biggest Gap Up" value={pct(s.biggestGapUp?.gapPct)} sub={s.biggestGapUp?.date} color={C.green} />
        <Tile label="Biggest Gap Down" value={pct(s.biggestGapDown?.gapPct)} sub={s.biggestGapDown?.date} color={C.red} />
        <Tile label="Up / Down Days" value={`${s.upDays} / ${s.downDays}`} sub={`${s.upPct}% up · ${s.tradingDays} sessions`} />
        <Tile label="Current Streak" value={`${s.currentStreak?.count || 0} ${s.currentStreak?.dir || ""}`} color={s.currentStreak?.dir === "up" ? C.green : s.currentStreak?.dir === "down" ? C.red : C.sub} />
        {s.avgVix != null && <Tile label="Avg VIX (ATM IV)" value={`${nfmt(s.avgVix, 1)}%`} sub={`${s.vixDays || 0} days with option data`} color={C.indigo} help="Day-wise ATM implied volatility — a VIX-like vol gauge" />}
        {s.pctChangeWithin1sd != null && <Tile label="Change in ±1σ" value={`${s.pctChangeWithin1sd}%`} sub={`${s.expMoveDays} days · target ~68%`} color={s.pctChangeWithin1sd >= 58 && s.pctChangeWithin1sd <= 80 ? C.green : C.amber} help="% of days whose actual close-to-close change stayed within the predicted ±1σ move. Calibrated ≈ 68%." />}
        {s.pctRangeWithin2sd != null && <Tile label="Range in ±2σ" value={`${s.pctRangeWithin2sd}%`} sub={`${s.expMoveDays} days · ±2σ ≈ median range`} color={C.indigo} help="% of days whose actual HIGH-LOW range stayed within the ±2σ band. Since ±2σ ≈ the typical range, this sits ~50-60% (not 95%); ✗ days had a wider-than-usual range." />}
        {s.calMult != null && <Tile label="Cal ×1σ" value={`${nfmt(s.calMult, 2)}×`} sub="EWMA 68th-%ile mult" color={C.amber} help="±1σ multiplier applied to raw IV σ = the EWMA-weighted empirical 68th percentile of |change|/IVσ. >1 = realised runs hotter than raw IV. Fat-tail + recency calibrated." />}
        {s.changeVs1sd != null && <Tile label="Change ≈ ±1σ" value={`${nfmt(s.changeVs1sd, 2)}×`} sub="median |change| ÷ ±1σ · ~1 = matches" color={s.changeVs1sd >= 0.7 && s.changeVs1sd <= 1.15 ? C.green : C.amber} help="±1σ predicts the day's CLOSE-TO-CLOSE change. Median ratio near 1 confirms the ±1σ band ≈ actual change." />}
        {s.rangeVs2sd != null && <Tile label="Range ≈ ±2σ" value={`${nfmt(s.rangeVs2sd, 2)}×`} sub="median range ÷ ±2σ · ~1 = matches" color={s.rangeVs2sd >= 0.8 && s.rangeVs2sd <= 1.2 ? C.green : C.amber} help="±2σ predicts the day's HIGH-LOW range (range ≈ 2× the close-to-close move). Median ratio near 1 confirms ±2σ band ≈ actual range." />}
      </div>

      {/* Weekday behaviour */}
      {s.byWeekday && (
        <div style={card({ padding: 16, marginBottom: 14 })}>
          <div style={{ fontSize: 13, fontWeight: 800, color: C.text, marginBottom: 4 }}>📅 Weekday Behaviour</div>
          <div style={{ fontSize: 12, color: C.sub, marginBottom: 12 }}>Kis din market kitni move karti hai — expiry-day / strike sizing ke liye.</div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 480 }}>
              <thead><tr>
                <th style={thL}>Weekday</th><th style={th}>Sessions</th><th style={th}>Avg Range</th><th style={th}>Avg Range %</th><th style={th}>Avg Move %</th><th style={th}>Up %</th>
              </tr></thead>
              <tbody>
                {(s.weekdays || ["Mon", "Tue", "Wed", "Thu", "Fri"]).map((wd) => {
                  const r = s.byWeekday[wd] || {};
                  return (
                    <tr key={wd}>
                      <td style={{ ...tdL, fontWeight: 700 }}>{wd}</td>
                      <td style={td}>{r.days || 0}</td>
                      <td style={td}>{cur(r.avgRange)}</td>
                      <td style={td}>{pct(r.avgRangePct)}</td>
                      <td style={{ ...td, color: signCol(r.avgChangePct) }}>{pct(r.avgChangePct)}</td>
                      <td style={td}>{r.upPct || 0}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Daily table */}
      <div style={card({ padding: 16 })}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
          <span style={{ fontSize: 13, fontWeight: 800, color: C.text }}>Day-wise data ({days.length})</span>
          <span style={{ fontSize: 11, color: C.muted }}>Exp Move: <b style={{ color: C.indigo }}>indigo</b> = IV-based · <b style={{ color: C.amber }}>amber ᴴ</b> = history-estimated (IV missing)</span>
        </div>
        <FilterableTable columns={columns} rows={days} initialSort={{ key: "date", dir: "desc" }} minWidth={1180} empty="Filter se koi din match nahi hua." />
      </div>
    </div>
  );
}

// ── IV / Straddle view ───────────────────────────────────────────────────────
function IvView({ data }) {
  const s = data.summary || {};
  const days = data.days || [];
  const cov = s.coverage || {};
  const columns = [
    { key: "date", label: "Date", align: "left", filter: "text" },
    { key: "dow", label: "Day", align: "left", filter: "select", color: () => C.sub },
    { key: "spot", label: "Spot", num: true, filter: "range", fmt: (v) => cur(v, 2) },
    { key: "atm", label: "ATM", num: true, filter: "range", fmt: (v) => nfmt(v) },
    { key: "expiry", label: "Expiry", align: "left", filter: "text", color: () => C.sub },
    { key: "dte", label: "DTE", num: true, filter: "range" },
    { key: "ce", label: "CE", num: true, filter: "range", fmt: (v) => cur(v, 2) },
    { key: "pe", label: "PE", num: true, filter: "range", fmt: (v) => cur(v, 2) },
    { key: "straddle", label: "Straddle", num: true, filter: "range", fmt: (v) => cur(v, 2), bold: true },
    { key: "straddlePct", label: "Str %", num: true, filter: "range", fmt: (v) => `${nfmt(v, 2)}%` },
    { key: "iv", label: "IV %", num: true, filter: "range", fmt: (v) => (v != null ? `${nfmt(v, 1)}%` : "—"), color: () => C.indigo, bold: true },
  ];
  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))", gap: 12, marginBottom: 14 }}>
        <Tile label="Avg ATM IV" value={s.avgIv != null ? `${nfmt(s.avgIv, 1)}%` : "—"} sub="Black-Scholes, ATM avg" color={C.indigo} />
        <Tile label="Max IV" value={s.maxIv ? `${nfmt(s.maxIv.value, 1)}%` : "—"} sub={s.maxIv?.date} color={C.red} />
        <Tile label="Min IV" value={s.minIv ? `${nfmt(s.minIv.value, 1)}%` : "—"} sub={s.minIv?.date} color={C.green} />
        <Tile label="Avg ATM Straddle" value={cur(s.avgStraddle, 1)} sub={`${pct(s.avgStraddlePct)} of spot`} color={C.amber} />
        <Tile label="Coverage" value={`${cov.withData || 0} / ${cov.total || 0}`} sub="days with option data" />
      </div>

      <div style={card({ padding: 12, marginBottom: 14, background: IS_CRYPTO_PORTAL ? "rgba(240,185,11,0.10)" : "#fffbeb", border: `1px solid ${IS_CRYPTO_PORTAL ? "rgba(240,185,11,0.35)" : "#fde68a"}`, fontSize: 12.5, color: IS_CRYPTO_PORTAL ? "#f0b90b" : "#92400e" })}>
        ℹ️ IV {IS_CRYPTO_PORTAL ? "agle din wali (next daily) expiry" : "nearest expiry (on/after each day)"} ke ATM CE+PE close se nikala gaya hai{IS_CRYPTO_PORTAL ? " (~1 day time value)" : ""}. Sirf un dino ke liye jinke liye option data downloaded hai — baaki din chhod diye gaye.
      </div>

      <div style={card({ padding: 16 })}>
        <div style={{ fontSize: 13, fontWeight: 800, color: C.text, marginBottom: 10 }}>Day-wise ATM IV & Straddle ({days.length})</div>
        <FilterableTable columns={columns} rows={days} initialSort={{ key: "date", dir: "desc" }} minWidth={820} empty="Filter se koi din match nahi hua." />
      </div>
    </div>
  );
}
